import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SolidButton } from "@components/button";
import { LoadingOverlay } from "@components/loading-overlay";
import { TopNav } from "@components/top-nav";
import CameraScanner, { cameraErrorMessage, isCameraSupported } from "../components/CameraScanner";
import PgConfirmModal from "../components/PgConfirmModal";
import {
	checkPgCampaign,
	getPgCampaign,
	getPgOperator,
	hasPgToken,
	pgLogout,
	scanPgCampaign,
	toPgError,
} from "../pgApi";

const formatTime = (iso) => {
	if (!iso) return "";
	const d = new Date(iso);
	return isNaN(d) ? iso : d.toLocaleString("zh-TW", { hour12: false });
};

/**
 * PG 單一掃碼入口：掃（或輸入）會員條碼 → 查詢 → 確認彈窗 → 後端判斷有券核銷／沒券補發並核銷 → 顯示結果。
 * 輸入框常駐 focus，手持掃碼槍（鍵盤模式，結尾送 Enter）可直接連續掃。
 */
const PgScanPage = () => {
	const { code } = useParams();
	const navigate = useNavigate();
	const inputRef = useRef(null);
	const operator = getPgOperator();

	const [campaign, setCampaign] = useState(null);
	const [campaignError, setCampaignError] = useState("");
	const [vipCode, setVipCode] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState(null); // { ok, vipCode, data | error }
	const [cameraOpen, setCameraOpen] = useState(false);
	const [pending, setPending] = useState(null); // 查詢通過、等待確認：{ value, inputMethod, data }

	const goLogin = useCallback(() => {
		navigate(`/pg/login?redirect=${encodeURIComponent(`/pg/${code}`)}`, { replace: true });
	}, [code, navigate]);

	const loadCampaign = useCallback(async () => {
		try {
			setCampaign(await getPgCampaign(code));
			setCampaignError("");
		} catch (error) {
			if (!hasPgToken()) return goLogin();
			setCampaignError(toPgError(error).message);
		}
	}, [code, goLogin]);

	useEffect(() => {
		loadCampaign();
	}, [loadCampaign]);

	const focusInput = () => setTimeout(() => inputRef.current?.focus(), 0);

	// 輸入框在活動載入前是 disabled，autoFocus 不會生效；開放兌換後再聚焦，掃碼槍一進頁面就能掃
	const isRedeemOpen = campaign?.is_redeem_open;
	useEffect(() => {
		if (isRedeemOpen) focusInput();
	}, [isRedeemOpen]);

	const showError = (error, value) => {
		if (!hasPgToken()) return goLogin();
		setResult({ ok: false, vipCode: value.toUpperCase(), error: toPgError(error) });
		// 開關可能剛被關掉，順便刷新活動狀態
		loadCampaign();
	};

	/**
	 * 第一步：掃描或送出後先查詢。可兌換才跳確認彈窗，不能兌換直接顯示原因。
	 * @param {string|undefined} rawValue 相機掃到的值；未傳時取輸入框
	 * @param {"camera"|"keyboard"} inputMethod 寫進後端掃碼紀錄
	 */
	const submit = async (rawValue, inputMethod = "keyboard") => {
		const value = (rawValue ?? vipCode).trim();
		if (!value || submitting || pending) return;

		setSubmitting(true);
		setResult(null);
		try {
			const res = await checkPgCampaign(code, value, inputMethod);
			setPending({ value, inputMethod, data: res.data });
		} catch (error) {
			showError(error, value);
			focusInput();
		} finally {
			setSubmitting(false);
			setVipCode("");
		}
	};

	/** 第二步：彈窗按「確認兌換」才真正核銷 */
	const confirmRedeem = async () => {
		if (!pending || submitting) return;

		setSubmitting(true);
		try {
			const res = await scanPgCampaign(code, pending.value, pending.inputMethod);
			setResult({ ok: true, vipCode: res.data.vip_code, data: res.data });
		} catch (error) {
			showError(error, pending.value);
		} finally {
			setSubmitting(false);
			closePending();
		}
	};

	// 彈窗開著時掃碼槍打進輸入框的字不會送出，關窗時一併清掉，避免 PG 誤以為已處理
	const closePending = () => {
		setPending(null);
		setVipCode("");
		focusInput();
	};

	const cancelRedeem = closePending;

	const showCameraError = (message) => {
		setResult({ ok: false, vipCode: "", error: { message } });
	};

	const onCameraScan = () => {
		// getUserMedia 只在 HTTPS（或 localhost）下可用
		if (!isCameraSupported()) {
			showCameraError("此瀏覽器無法使用相機（需使用 HTTPS 連線），請改用手動輸入");
			return;
		}
		setResult(null);
		setCameraOpen(true);
	};

	const onLogout = async () => {
		await pgLogout();
		goLogin();
	};

	const isOpen = campaign?.is_redeem_open;

	return (
		<div className="flex min-h-svh flex-col bg-white">
			<TopNav />
			<header className="flex items-center justify-between border-y border-[#e5e5e5] px-5 py-4">
				<div>
					<p className="noto-sans-tc-medium text-[17px] text-[#292929]">
						{campaign?.name ?? "現場兌換"}
					</p>
					<p className="text-[12px] text-[#a5a5a5]">{operator?.name}</p>
				</div>
				<button className="text-[13px] text-[#5c5c5c] underline" onClick={onLogout}>
					登出
				</button>
			</header>

			<main className="flex flex-1 flex-col px-5 py-6">
				{campaignError && (
					<p className="mb-4 rounded-[6px] bg-[#fff1f1] p-4 text-center text-[#ff4750]">
						{campaignError}
					</p>
				)}

				{campaign && !isOpen && (
					<p className="mb-4 rounded-[6px] bg-[#f4f4f4] p-4 text-center text-[16px] text-[#5c5c5c]">
						目前暫停兌換
					</p>
				)}

				<form
					onSubmit={(e) => {
						e.preventDefault();
						submit();
					}}
				>
					<label className="mb-2 block text-[14px] text-[#5c5c5c]" htmlFor="pg-vip-code">
						會員條碼
					</label>
					<input
						id="pg-vip-code"
						ref={inputRef}
						autoFocus
						autoComplete="off"
						autoCapitalize="characters"
						className="mb-4 block w-full rounded-[6px] border border-[#a8a8a8] px-4 py-3 text-[18px] tracking-wider focus:border-[#292929] focus:outline-none focus:ring-0"
						placeholder="掃描或輸入會員條碼"
						value={vipCode}
						onChange={(e) => setVipCode(e.target.value)}
						disabled={!isOpen}
					/>
					<div className="flex gap-3">
						<SolidButton
							label={submitting ? "處理中…" : "兌換"}
							buttonClass={
								"flex-1 rounded-[6px] py-3 text-[16px]" +
								(!isOpen || !vipCode.trim() || submitting ? " opacity-50" : "")
							}
						/>
						<button
							type="button"
							className="noto-sans-tc-medium flex-1 rounded-[6px] border border-[#292929] py-3 text-[16px] text-[#292929] disabled:opacity-50"
							onClick={onCameraScan}
							disabled={!isOpen || submitting}
						>
							相機掃描
						</button>
					</div>
				</form>

				{result && (
					<section
						className={
							"mt-8 rounded-[8px] p-6 text-center " +
							(result.ok ? "bg-[#eef8f0]" : "bg-[#fff1f1]")
						}
					>
						<p
							className={
								"noto-sans-tc-medium mb-2 text-[24px] " +
								(result.ok ? "text-[#1f7a35]" : "text-[#ff4750]")
							}
						>
							{result.ok ? "兌換成功" : "無法兌換"}
						</p>
						{result.vipCode && (
							<p className="mb-1 text-[15px] tracking-wider text-[#292929]">{result.vipCode}</p>
						)}
						{result.ok ? (
							<>
								{result.data.member_name && (
									<p className="mb-1 text-[15px] text-[#292929]">{result.data.member_name}</p>
								)}
								<p className="text-[14px] text-[#5c5c5c]">
									{result.data.issued_this_scan ? "新會員（現場發券）" : "既有會員"}
								</p>
							</>
						) : (
							<>
								<p className="text-[15px] text-[#292929]">{result.error.message}</p>
								{result.error.data?.redeemed_at && (
									<p className="mt-1 text-[13px] text-[#5c5c5c]">
										兌換時間：{formatTime(result.error.data.redeemed_at)}
									</p>
								)}
							</>
						)}
					</section>
				)}
			</main>

			<CameraScanner
				open={cameraOpen}
				onDetected={(value) => {
					setCameraOpen(false);
					submit(value, "camera");
				}}
				onClose={() => {
					setCameraOpen(false);
					focusInput();
				}}
				onError={(error) => {
					setCameraOpen(false);
					showCameraError(cameraErrorMessage(error));
					focusInput();
				}}
			/>

			<PgConfirmModal
				pending={pending}
				submitting={submitting}
				onConfirm={confirmRedeem}
				onCancel={cancelRedeem}
				onClosed={focusInput}
			/>

			<LoadingOverlay visible={!campaign && !campaignError} />
		</div>
	);
};

export default PgScanPage;
